import type { CallCategory, SalesChecklistBehaviors } from "@expert/shared-types";

import { guidedFlowRecommendationMap } from "@expert/solve-tools";
import { Button, Chip, Divider, Group, Select, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";

import {
    emitDeviceCue,
    emitGuideRecommendation,
    emitLocationCues,
    emitSalesChecklistBehavior,
    simulateOrchestratorError,
    simulateSilentTimeout,
} from "./GaiaTab.helpers";

type GaiaTabProps = {
    partner: string;
    sessionId: string;
};

interface LocationCuePreset {
    label: string;
    city: string;
    state: string;
}

const LOCATION_PRESETS: LocationCuePreset[] = [
    { label: "San Diego, CA", city: "San Diego", state: "California" },
    { label: "New York, NY", city: "New York", state: "New York" },
    { label: "Austin, TX", city: "Austin", state: "Texas" },
    { label: "Seattle, WA", city: "Seattle", state: "Washington" },
    { label: "Miami, FL", city: "Miami", state: "Florida" },
    { label: "Chicago, IL", city: "Chicago", state: "Illinois" },
];

const DEVICE_PRESETS = ["Tablet", "Laptop", "Smartwatch", "Headphones"];

const SALES_CHECKLIST_BEHAVIORS: SalesChecklistBehaviors[] = [
    "PROTECTION",
    "TECH_SUPPORT",
    "FUTURE_DEVICES",
    "NON_PARTNER_DEVICES",
    "IN_HOME_INSTALLATION",
    "IN_STORE_SERVICES",
    "ASK_SALE",
    "BILLING",
    "COVERAGE",
    "TRANSITION",
    "RAPPORT",
    "ENGAGEMENT_QUESTION",
    "DISCOVERY_QUESTION",
    "ENROLLMENT_CELEBRATION",
    "ASK_SEND_LINK",
];

export function GaiaTab({ partner, sessionId }: GaiaTabProps) {
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [deviceName, setDeviceName] = useState("");

    return (
        <Stack gap="sm">
            <Divider label="Location Cues" labelPosition="left" />

            <Select
                placeholder="Quick select location..."
                data={LOCATION_PRESETS.map((p) => ({ value: `${p.city}|${p.state}`, label: p.label }))}
                onChange={(value) => {
                    if (value) {
                        const [c, s] = value.split("|");
                        emitLocationCues(partner, sessionId, c, s, setCity, setState);
                    }
                }}
                clearable
                size="xs"
                comboboxProps={{ zIndex: 10000 }}
            />

            <Group grow>
                <TextInput placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} size="xs" />
                <TextInput placeholder="State" value={state} onChange={(e) => setState(e.target.value)} size="xs" />
            </Group>
            <Button
                size="xs"
                variant="light"
                onClick={() => emitLocationCues(partner, sessionId, city, state, setCity, setState)}
                disabled={!city && !state}
            >
                Emit Location
            </Button>

            <Divider label="Device Cues" labelPosition="left" />

            <Select
                placeholder="Quick select device..."
                data={DEVICE_PRESETS.map((value) => ({ value, label: value }))}
                onChange={(value) => {
                    if (value) emitDeviceCue(partner, sessionId, value, setDeviceName);
                }}
                clearable
                size="xs"
                comboboxProps={{ zIndex: 10000 }}
            />

            <Group>
                <TextInput
                    placeholder="Custom device name"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    size="xs"
                    style={{ flex: 1 }}
                />
                <Button
                    size="xs"
                    variant="light"
                    onClick={() => emitDeviceCue(partner, sessionId, deviceName, setDeviceName)}
                    disabled={!deviceName}
                >
                    Emit
                </Button>
            </Group>

            <Divider label="Guided Flow Recommendations" labelPosition="left" />

            <Group gap="xs">
                {Object.entries(guidedFlowRecommendationMap).map(([callCategory, label]) =>
                    label ? (
                        <Chip
                            key={callCategory}
                            size="xs"
                            variant="filled"
                            color="blue"
                            checked={false}
                            onClick={() => emitGuideRecommendation(partner, sessionId, callCategory as CallCategory)}
                        >
                            {label}
                        </Chip>
                    ) : null,
                )}
            </Group>

            <Divider label="Sales Checklist Behaviors" labelPosition="left" />

            <Group gap="xs">
                {SALES_CHECKLIST_BEHAVIORS.map((behavior) => (
                    <Chip
                        key={behavior}
                        size="xs"
                        variant="light"
                        checked={false}
                        onClick={() => emitSalesChecklistBehavior(partner, sessionId, behavior)}
                    >
                        {behavior.replace(/_/g, " ")}
                    </Chip>
                ))}
            </Group>

            <Divider label="GAIA Error Simulation" labelPosition="left" />

            <Text size="xs" c="dimmed">
                Simulate GAIA failure scenarios to test client-side error handling
            </Text>
            <Button
                size="xs"
                variant="light"
                color="red"
                onClick={() => simulateSilentTimeout(sessionId)}
                disabled={!sessionId}
            >
                Simulate Silent Timeout
            </Button>
            <Button
                size="xs"
                variant="light"
                color="orange"
                onClick={() => simulateOrchestratorError(sessionId)}
                disabled={!sessionId}
            >
                Simulate Orchestrator Error (500)
            </Button>
        </Stack>
    );
}
